/**
* 11_yogaNidra_body.tsx
* Body-only version of HTLDSlide11 — no header, no login/logout, no logo.
* Header row (breadcrumb + right-aligned title) and ScaleToFit added to match
* slides 1–10.
*/

import React, { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { NIDRA_STEPS, NIDRA_FALLBACK_SEC, nidraAudioFile, nidraStepLabel, nidraExhaleAt } from "./yogaNidraScan";
import { nidraContent, useNidra, Seg } from "./yoganidra.data";
import { pick } from "i18n/lang";
import { localAudio } from "i18n/audio";
import { useIsPortrait } from "./useIsPortrait";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

const accent = "#c090e0";
const font   = FONT_BODY;

if (typeof document !== "undefined" && !document.getElementById("yogyism-pulse-c090e0")) {
 const s = document.createElement("style");
 s.id = "yogyism-pulse-c090e0";
 s.textContent = `
   @keyframes yogyismPulse_c090e0 {
     0%   { box-shadow: 0 0 0 0 rgba(192,144,224,0.70), 0 0 12px 2px rgba(192,144,224,0.45); transform: scale(1); }
     50%  { box-shadow: 0 0 0 16px rgba(192,144,224,0), 0 0 20px 6px rgba(192,144,224,0.65); transform: scale(1.12); }
     100% { box-shadow: 0 0 0 0 rgba(192,144,224,0), 0 0 12px 2px rgba(192,144,224,0.45); transform: scale(1); }
   }`;
 document.head.appendChild(s);
}

const S: Record<string, CSSProperties> = {
 root:    { background:"#040f18", fontFamily:font, color:"#d0e8f8", width:"100%", height:"100%", display:"flex", flexDirection:"column", boxSizing:"border-box", overflow:"hidden" },
 // Shared header row: breadcrumb (left) + slide title (right), above the divider line
 subtitleBar: {
   background: "#050f1a", borderBottom: "1px solid rgba(192,144,224,0.12)",
   padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
   display: "flex", alignItems: "center", justifyContent: "space-between",
   gap: "clamp(12px,2vw,28px)", flexShrink: 0,
 },
 body:    { flex:1, minHeight:0, display:"flex", gap:"clamp(16px,2vw,28px)", padding:"clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)", alignItems:"stretch", overflow:"hidden" },
 content: { flex:1, minWidth:"min(100%, 380px)", display:"flex", flexDirection:"column", gap:"clamp(8px,1vw,12px)", overflow:"hidden" },
 panel:   { width:"clamp(220px,28vw,380px)", flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", overflow:"hidden" },
};

/* ─── Body Scan Canvas ───────────────────────────────────────────────────── */
const BodyScanCanvasInner: React.FC<{
  stepNameRef: React.MutableRefObject<string>;
  playing: boolean;
  onComplete?: () => void;
  }> = ({ stepNameRef, playing, onComplete }) => {
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const st = useRef({
    animId:         0,
    playing:        false,
    partIdx:        0,
    fallbackId:     0,
    introTimeoutId: 0,
    drawAnim:       null as (() => void) | null,
    drawStatic:     null as (() => void) | null,
    stopAudio:      null as (() => void) | null,
    startWithIntro: null as (() => void) | null,
  });
  
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
    const tx = pick(nidraContent).canvas; // slides remount on language change
    const W = 400, H = 560; canvas.width = W; canvas.height = H;
    const cx = 200;
    const R_MIN = 16, R_MAX = 30;
    const SCALE = 0.79, BODY_X = cx - 103.5 * SCALE, BODY_Y = 64;
  
    interface Def { left: number; top: number; w: number; h: number; path: string; }
    const DEFS: Record<string, Def> = {
      head:     { left: 75,   top: -6,  w: 56.594,  h: 95.031,  path: "M15.92 68.5l8.8 12.546 3.97 13.984-9.254-7.38-4.622-15.848zm27.1 0l-8.8 12.546-3.976 13.988 9.254-7.38 4.622-15.848zm6.11-27.775l.108-11.775-21.16-14.742L8.123 26.133 8.09 40.19l-3.24.215 1.462 9.732 5.208 1.81 2.36 11.63 9.72 11.018 10.856-.324 9.56-10.37 1.918-11.952 5.207-1.81 1.342-9.517zm-43.085-1.84l-.257-13.82L28.226 11.9l23.618 15.755-.216 10.37 4.976-17.085L42.556 2.376 25.49 0 10.803 3.673.002 24.415z" },
      shoulder: { left: 50,   top: 69,  w: 109.532, h: 46.594,  path: "M38.244-.004l1.98 9.232-11.653 2.857-7.474-2.637zm33.032 0l-1.98 9.232 11.653 2.857 7.474-2.637zm21.238 10.54l4.044-2.187 12.656 14 .07 5.33S92.76 10.66 92.515 10.535zm-1.285.58c-.008.28 17.762 18.922 17.762 18.922l.537 16.557-6.157-10.55L91.5 30.988 83.148 15.6zm-74.224-.58L12.962 8.35l-12.656 14-.062 5.325s16.52-17.015 16.764-17.14zm1.285.58C18.3 11.396.528 30.038.528 30.038L-.01 46.595l6.157-10.55 11.87-5.056L26.374 15.6z" },
      arm:      { left: 25.5, top: 112, w: 156.344, h: 119.25,  path: "M21.12 56.5a1.678 1.678 0 0 1-.427.33l.935 8.224 12.977-13.89 1.2-8.958A168.2 168.2 0 0 0 21.12 56.5zm1.387 12.522l-18.07 48.91 5.757 1.333 19.125-39.44 3.518-22.047zm-5.278-18.96l2.638 18.74-17.2 46.023L.01 113.05l6.644-35.518zm118.015 6.44a1.678 1.678 0 0 0 .426.33l-.934 8.222-12.977-13.89-1.2-8.958A168.2 168.2 0 0 1 135.24 56.5zm-1.39 12.52l18.073 48.91-5.758 1.333-19.132-39.44-3.52-22.05zm5.28-18.96l-2.64 18.74 17.2 46.023 2.658-1.775-6.643-35.518zm-103.1-12.323a1.78 1.78 0 0 1 .407-.24l3.666-27.345L33.07.015l-7.258 10.58-6.16 37.04.566 4.973a151.447 151.447 0 0 1 15.808-14.87zm84.3 0a1.824 1.824 0 0 0-.407-.24l-3.666-27.345L123.3.015l7.258 10.58 6.16 37.04-.566 4.973a151.447 151.447 0 0 0-15.822-14.87zM22.288 8.832l-3.3 35.276-2.2-26.238zm111.79 0l3.3 35.276 2.2-26.238z" },
      chest:    { left: 60,   top: 88,  w: 86.594,  h: 45.063,  path: "M19.32 0l-9.225 16.488-10.1 5.056 6.15 4.836 4.832 14.07 11.2 4.616 17.85-8.828-4.452-34.7zm47.934 0l9.225 16.488 10.1 5.056-6.15 4.836-4.833 14.07-11.2 4.616-17.844-8.828 4.45-34.7z" },
      stomach:  { left: 66,   top: 130, w: 75.25,   h: 107.594, path: "M19.25 7.49l16.6-7.5-.5 12.16-14.943 7.662zm-10.322 8.9l6.9 3.848-.8-9.116zm5.617-8.732L1.32 2.15 6.3 15.6zm-8.17 9.267l9.015 5.514 1.54 11.028-8.795-5.735zm15.53 5.89l.332 8.662 12.286-2.665.664-11.826zm14.61 84.783L33.28 76.062l-.08-20.53-11.654-5.736-1.32 37.5zM22.735 35.64L22.57 46.3l11.787 3.166.166-16.657zm-14.16-5.255L16.49 35.9l1.1 11.25-8.8-7.06zm8.79 22.74l-9.673-7.28-.84 9.78L-.006 68.29l10.564 14.594 5.5.883 1.98-20.735zM56 7.488l-16.6-7.5-.5 12.16 14.942 7.66zm10.32 8.9l-6.9 3.847.8-9.116zm-5.617-8.733L73.93 2.148l-4.98 13.447zm8.17 9.267l-9.015 5.514-1.54 11.03 8.8-5.736zm-15.53 5.89l-.332 8.662-12.285-2.665-.664-11.827zm-14.61 84.783l3.234-31.536.082-20.532 11.65-5.735 1.32 37.5zm13.78-71.957l.166 10.66-11.786 3.168-.166-16.657zm14.16-5.256l-7.915 5.514-1.1 11.25 8.794-7.06zm-8.79 22.743l9.673-7.28.84 9.78 6.862 12.66-10.564 14.597-5.5.883-1.975-20.74z" },
      legs:     { left: 57,   top: 205, w: 93.626,  h: 286.625, path: "M17.143 138.643l-.664 5.99 4.647 5.77 1.55 9.1 3.1 1.33 2.655-13.755 1.77-4.88-1.55-3.107zm20.582.444l-3.32 9.318-7.082 13.755 1.77 12.647 5.09-14.2 4.205-7.982zm-26.557-12.645l5.09 27.29-3.32-1.777-2.656 8.875zm22.795 42.374l-1.55 4.88-3.32 20.634-.442 27.51 4.65 26.847-.223-34.39 4.87-13.754.663-15.087zM23.34 181.24l1.106 41.267 8.853 33.28-9.628-4.55-16.045-57.8 5.533-36.384zm15.934 80.536l-.664 18.415-1.55 6.435h-4.647l-1.327-4.437-1.55-.222.332 4.437-5.864-1.778-1.55-.887-6.64-1.442-.22-5.214 6.418-10.87 4.426-5.548 10.844-4.437zM13.63 3.076v22.476l15.71 31.073 9.923 30.85L38.23 66.1zm25.49 30.248l.118-.148-.793-2.024L21.9 12.992l-1.242-.44L31.642 40.93zM32.865 44.09l6.812 17.6 2.274-21.596-1.344-3.43zM6.395 61.91l.827 25.34 12.816 35.257-3.928 10.136L3.5 88.133zM30.96 74.69l.345.826 6.47 15.48-4.177 38.342-6.594-3.526 5.715-35.7zm45.5 63.953l.663 5.99-4.647 5.77-1.55 9.1-3.1 1.33-2.655-13.755 1.77-4.88 1.55-3.107zm-20.582.444l3.32 9.318 7.08 13.755-1.77 12.647-5.09-14.2-4.2-7.987zm3.762 29.73l1.55 4.88 3.32 20.633.442 27.51-4.648 26.847.22-34.39-4.867-13.754-.67-15.087zm10.623 12.424l-1.107 41.267-8.852 33.28 9.627-4.55 16.046-57.8-5.533-36.384zM54.33 261.777l.663 18.415 1.546 6.435h4.648l1.328-4.437 1.55-.222-.333 4.437 5.863-1.778 1.55-.887 6.638-1.442.222-5.214-6.418-10.868-4.426-5.547-10.844-4.437zm25.643-258.7v22.476L64.26 56.625l-9.923 30.85L55.37 66.1zM54.48 33.326l-.118-.15.793-2.023L71.7 12.993l1.24-.44L61.96 40.93zm6.255 10.764l-6.812 17.6-2.274-21.595 1.344-3.43zm26.47 17.82l-.827 25.342-12.816 35.256 3.927 10.136 12.61-44.51zM62.64 74.693l-.346.825-6.47 15.48 4.178 38.342 6.594-3.527-5.715-35.7zm19.792 51.75l-5.09 27.29 3.32-1.776 2.655 8.875zM9.495-.007l.827 21.373-7.028 42.308-3.306-34.155zm2.068 27.323L26.24 59.707l3.307 26-6.2 36.58L9.91 85.046l-.827-38.342zM84.103-.01l-.826 21.375 7.03 42.308 3.306-34.155zm-2.066 27.325L67.36 59.707l-3.308 26 6.2 36.58 13.436-37.24.827-38.34z" },
      hands:    { left: 1,    top: 224, w: 205,     h: 38.938,  path: "M21.255-.002l2.88 6.9 8.412 1.335.664 12.458-4.427 17.8-2.878-.22 2.8-11.847-2.99-.084-4.676 12.6-3.544-.446 4.4-12.736-3.072-.584-5.978 13.543-4.428-.445 6.088-14.1-2.1-1.25-7.528 12.012-3.764-.445L12.4 12.9l-1.107-1.78L.665 15.57 0 13.124l8.635-7.786zm162.49 0l-2.88 6.9-8.412 1.335-.664 12.458 4.427 17.8-2.878-.22-2.8-11.847 2.99-.084 4.676 12.6 3.544-.446-4.4-12.736 3.072-.584 5.978 13.543 4.428-.445-6.088-14.1 2.1-1.25 7.528 12.012 3.764-.445L192.6 12.9l1.107-1.78 10.628 4.45.665-2.447-8.635-7.786z" },
    };
  
    const PATHS: Record<string, Path2D> = {};
    Object.keys(DEFS).forEach(k => { PATHS[k] = new Path2D(DEFS[k].path); });

    // One detailed pass, feet → crown → whole body (see yogaNidraScan.ts).
    const steps = NIDRA_STEPS;
  
    const stars = [[15,22,0.9],[80,12,0.7],[316,16,1.0],[384,44,0.7],[36,68,0.8],[356,62,0.9],[388,8,0.9],[116,44,0.7]];
    function easeInOut(t: number) { return t < 0.5 ? 2*t*t : -1+(4-2*t)*t; }
  
    // ── Audio: intro + one clip per body part (played in sequence) ─────────────
    // Each part's clip plays, its body region lights up; on 'ended' the next part
    // begins. Highlight is driven by which clip is playing → perfect sync.
    // Clips load lazily (46 of them): each one fetches the next while it plays.
    const introAudio = localAudio("/audio/intro-body-scan.mp3"); introAudio.preload = "auto";
    const partAudio: HTMLAudioElement[] = steps.map(s => {
      const a = localAudio(nidraAudioFile(s.name)); a.preload = "none"; return a;
    });
  
    function stopAllAudio() {
      clearTimeout(st.current.fallbackId);
      introAudio.onended = null;
      try { introAudio.pause(); introAudio.currentTime = 0; } catch (_) {}
      partAudio.forEach(a => { a.onended = null; a.onerror = null; try { a.pause(); a.currentTime = 0; } catch (_) {} });
    }
  
    function drawPart(key: string, clipY: [number, number] | null, fill: string, stroke: string, shadowC: string, shadowB: number, lw: number) {
      const def = DEFS[key];
      const path = PATHS[key];
      ctx.save();
      ctx.translate(BODY_X + def.left * SCALE, BODY_Y + def.top * SCALE);
      ctx.scale(SCALE, SCALE);
      if (clipY) { ctx.beginPath(); ctx.rect(-4, clipY[0] - 2, def.w + 8, (clipY[1] - clipY[0]) + 4); ctx.clip(); }
      ctx.shadowColor = shadowC; ctx.shadowBlur = shadowB;
      ctx.fillStyle = fill; ctx.fill(path);
      ctx.shadowBlur = 0;
      ctx.strokeStyle = stroke; ctx.lineWidth = lw / SCALE; ctx.stroke(path);
      ctx.restore();
    }
  
    function drawBg() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);
      stars.forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fillStyle = "rgba(255,255,255,0.48)"; ctx.fill(); });
      ctx.beginPath(); ctx.arc(384, 36, 20, 0, Math.PI * 2); ctx.fillStyle = "rgba(220,200,255,0.08)"; ctx.fill();
      ctx.beginPath(); ctx.arc(384, 36, 12, 0, Math.PI * 2); ctx.fillStyle = "rgba(230,215,255,0.15)"; ctx.fill();
      ctx.beginPath(); ctx.arc(384, 36,  8, 0, Math.PI * 2); ctx.fillStyle = "#f5f0ff"; ctx.fill();
    }
  
    function drawStatic() {
      drawBg();
      Object.keys(DEFS).forEach(key => drawPart(key, null, "rgba(192,144,224,0.08)", "rgba(192,144,224,0.65)", "transparent", 0, 0.8));
    }
  
    function drawAnim() {
      if (!st.current.playing) return;
      drawBg();
      const cosmetic = performance.now() / 16;
      const partIdx  = st.current.partIdx;
      const active   = steps[partIdx];
      const a        = partAudio[partIdx];
      const dur      = (a && isFinite(a.duration) && a.duration > 0) ? a.duration : NIDRA_FALLBACK_SEC;
      const t        = a ? a.currentTime : 0;
      // Inhale lasts until "Exhale" is spoken (measured per English clip; 40 % for German).
      const INH      = nidraExhaleAt(active, dur);
      const isInhale = t < INH;
      const bProg    = isInhale ? (t / INH) : Math.min(1, (t - INH) / (dur - INH));
      const radius   = isInhale ? R_MIN + (R_MAX - R_MIN) * easeInOut(bProg) : R_MAX - (R_MAX - R_MIN) * easeInOut(bProg);
      const glowInt  = 0.55 + 0.45 * Math.sin(bProg * Math.PI);
      const phaseDur = isInhale ? INH : (dur - INH);
      const count    = Math.max(1, Math.ceil(phaseDur * (1 - bProg)));
  
      stepNameRef.current = nidraStepLabel(active);
  
      const circCY = 42;
      const breathFill = (radius - R_MIN) / (R_MAX - R_MIN);
      const haloR = radius + 24;
      const halo = ctx.createRadialGradient(cx, circCY, radius * 0.4, cx, circCY, haloR);
      halo.addColorStop(0,   `rgba(192,144,224,${(0.20 + 0.22 * breathFill).toFixed(3)})`);
      halo.addColorStop(0.6, `rgba(192,144,224,${(0.06 + 0.08 * breathFill).toFixed(3)})`);
      halo.addColorStop(1,   "rgba(192,144,224,0)");
      ctx.beginPath(); ctx.arc(cx, circCY, haloR, 0, Math.PI * 2); ctx.fillStyle = halo; ctx.fill();
      for (let i = 0; i < 4; i++) {
        const rp = ((cosmetic * 0.5 + i * 25) % 100) / 100;
        const rr = radius + 3 + rp * 25;
        const ra = (1 - rp) * (0.14 + 0.18 * breathFill);
        ctx.beginPath(); ctx.arc(cx, circCY, rr, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(192,144,224,${ra.toFixed(3)})`; ctx.lineWidth = 1.1; ctx.stroke();
      }
      const og = ctx.createRadialGradient(cx - radius*0.3, circCY - radius*0.3, 2, cx, circCY, radius);
      og.addColorStop(0,    `rgba(232,214,255,${(0.45 + 0.25 * breathFill).toFixed(3)})`);
      og.addColorStop(0.45, `rgba(192,144,224,${(0.26 + 0.12 * breathFill).toFixed(3)})`);
      og.addColorStop(1,    "rgba(192,144,224,0.05)");
      ctx.beginPath(); ctx.arc(cx, circCY, radius, 0, Math.PI * 2); ctx.fillStyle = og; ctx.fill();
      const sa = (cosmetic * 0.02) % (Math.PI*2);
      ctx.beginPath(); ctx.arc(cx, circCY, radius*0.82, sa, sa + Math.PI*0.6);
      ctx.strokeStyle = `rgba(232,214,255,${(0.18 + 0.18 * breathFill).toFixed(3)})`; ctx.lineWidth = 1.6; ctx.stroke();
      ctx.strokeStyle = `rgba(192,144,224,${(0.72 + 0.22 * breathFill).toFixed(3)})`;
      ctx.lineWidth = 2; ctx.stroke();
  
      ctx.textAlign = "center";
      ctx.font = "bold 13px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle = accent;
      ctx.fillText(isInhale ? tx.inhale : tx.exhale, cx, circCY - 9);
      ctx.font = "bold 30px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle = "rgba(192,144,224,0.90)";
      ctx.textBaseline = "middle"; ctx.fillText(String(count), cx, circCY + 7); ctx.textBaseline = "alphabetic";
  
      Object.keys(DEFS).forEach(key => drawPart(key, null, "rgba(192,144,224,0.08)", "rgba(192,144,224,0.60)", "transparent", 0, 0.8));
      for (let i = 0; i < partIdx; i++) {
        const s = steps[i];
        drawPart(s.svgKey, s.clipY, "rgba(192,144,224,0.10)", "rgba(205,168,255,0.22)", "transparent", 0, 0.5);
      }
      // "*" (Whole Body) lights every part of the figure.
      const activeParts: [string, [number, number] | null][] =
        active.svgKey === "*" ? Object.keys(DEFS).map(k => [k, null]) : [[active.svgKey, active.clipY]];
      activeParts.forEach(([key, clip]) =>
        drawPart(key, clip, `rgba(218,182,255,${0.75 * glowInt})`, `rgba(242,218,255,${0.88 * glowInt})`, `rgba(210,168,255,${0.85 * glowInt})`, 16 * glowInt, 0.8));
  
      // Phase bar (INHALE / EXHALE) — middle row
      const barY = 466, barX = 40, barW = W - 80;
      [{ l: tx.inhale, w: 1.25 }, { l: tx.exhale, w: 1.75 }].forEach((seg, i) => {
        const sx = barX + i * (barW / 2), sw = barW / 2;
        const cur = (i === 0 && isInhale) || (i === 1 && !isInhale);
        ctx.fillStyle = cur ? "rgba(192,144,224,0.28)" : "rgba(192,144,224,0.07)"; ctx.fillRect(sx, barY, sw - 2, 18);
        ctx.strokeStyle = cur ? "rgba(192,144,224,0.55)" : "rgba(192,144,224,0.12)"; ctx.lineWidth = 1; ctx.strokeRect(sx, barY, sw - 2, 18);
        ctx.font = "10px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle = cur ? accent : "rgba(192,144,224,0.28)";
        ctx.textAlign = "center"; ctx.fillText(seg.l, sx + (sw - 2) / 2, barY + 12);
      });

      // FEET ── progress ── CROWN — bottom row, clear of the phase bar
      const pX = 28, pY = 508, pW = W - 56;
      const overall = (partIdx + Math.min(1, t / dur)) / steps.length;
      ctx.font = "10px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle = "rgba(192,144,224,0.30)";
      ctx.textAlign = "left";  ctx.fillText(tx.feet,  pX,      pY - 6);
      ctx.textAlign = "right"; ctx.fillText(tx.crown, pX + pW, pY - 6);
      ctx.fillStyle = "rgba(192,144,224,0.10)"; ctx.fillRect(pX, pY, pW, 3);
      const pG = ctx.createLinearGradient(pX, 0, pX + pW, 0);
      pG.addColorStop(0, "rgba(155,108,208,0.65)"); pG.addColorStop(1, "rgba(220,185,255,0.85)");
      ctx.fillStyle = pG; ctx.fillRect(pX, pY, pW * Math.min(1, overall), 3);
      // Point counter — very bottom, its own line, clear of everything
      ctx.textAlign = "center"; ctx.fillStyle = "rgba(192,144,224,0.55)";
      ctx.font = "12px 'Space Grotesk',Arial,sans-serif";
      ctx.fillText(tx.point(partIdx + 1, steps.length), cx, pY + 20);
  
      st.current.animId = requestAnimationFrame(drawAnim);
    }
  
    function playPart(i: number) {
      const a = partAudio[i];
      if (!a) return;
      const nextA = partAudio[i + 1];
      if (nextA && nextA.preload !== "auto") { nextA.preload = "auto"; nextA.load(); }
      let done = false;
      const advance = () => {
        if (done || !st.current.playing) return;
        done = true;
        a.onended = null; a.onerror = null;
        clearTimeout(st.current.fallbackId);
        const next = st.current.partIdx + 1;
        if (next >= steps.length) {
          st.current.playing = false;
          stopAllAudio();
          drawStatic();
          onCompleteRef.current?.();
          return;
        }
        st.current.partIdx = next;
        playPart(next);
      };
      // Missing or broken clip: hold the point for the usual time, then move on
      // (otherwise 'ended' never fires and the scan would stall here).
      const fallback = () => {
        clearTimeout(st.current.fallbackId);
        st.current.fallbackId = window.setTimeout(advance, NIDRA_FALLBACK_SEC * 1000);
      };
      a.onended = advance;
      a.onerror = fallback;
      a.currentTime = 0;
      a.play().catch(fallback);
    }
  
    function beginScan() {
      clearTimeout(st.current.introTimeoutId);
      if (st.current.playing) return;
      st.current.playing = true;
      st.current.partIdx = 0;
      playPart(0);
      st.current.drawAnim?.();
    }
  
    function startWithIntro() {
      partAudio[0].preload = "auto"; partAudio[0].load();
      if (!introAudio) { beginScan(); return; }
      introAudio.onended = () => {
        introAudio.onended = null;
        st.current.introTimeoutId = window.setTimeout(beginScan, 1000);
      };
      introAudio.currentTime = 0;
      introAudio.play().then(() => {
        const dur = introAudio.duration;
        const ms  = isFinite(dur) ? Math.ceil(dur * 1000) + 2000 : 120000;
        st.current.introTimeoutId = window.setTimeout(beginScan, ms);
      }).catch(beginScan);
    }
  
    st.current.drawAnim       = drawAnim;
    st.current.drawStatic     = drawStatic;
    st.current.stopAudio      = stopAllAudio;
    st.current.startWithIntro = startWithIntro;
    drawStatic();
  
    const cur = st.current;
    return () => {
      cancelAnimationFrame(cur.animId);
      clearTimeout(cur.introTimeoutId);
      stopAllAudio();
    };
  }, []); // eslint-disable-line
  
  useEffect(() => {
    const s = st.current;
    cancelAnimationFrame(s.animId);
    clearTimeout(s.introTimeoutId);
    s.playing = false;
    s.stopAudio?.();
    if (playing) {
      s.partIdx = 0;
      stepNameRef.current = "";
      s.startWithIntro?.();
    } else {
      s.drawStatic?.();
    }
  }, [playing, stepNameRef]);
  
  return (
    <canvas ref={canvasRef} style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }} />
  );
  };

const BodyScanPanel:React.FC=()=>{
 const labelRef=useRef<HTMLDivElement>(null);const stepNameRef=useRef<string>("");const[playing,setPlaying]=useState(false);
 const audioCtx=useRef<AudioContext|null>(null);const masterGain=useRef<GainNode|null>(null);const oscs=useRef<OscillatorNode[]>([]);const lfo=useRef<OscillatorNode|null>(null);const binOscs=useRef<OscillatorNode[]>([]);const binGain=useRef<GainNode|null>(null);
 const startAudio=()=>{const ctx=new(window.AudioContext||(window as any).webkitAudioContext)();audioCtx.current=ctx;const gain=ctx.createGain();gain.gain.setValueAtTime(0,ctx.currentTime);gain.gain.linearRampToValueAtTime(0.04,ctx.currentTime+2.0);gain.connect(ctx.destination);masterGain.current=gain;const delay=ctx.createDelay(1.0);const fbG=ctx.createGain();delay.delayTime.value=0.6;fbG.gain.value=0.30;delay.connect(fbG);fbG.connect(delay);delay.connect(gain);oscs.current=([[73.4,0.55],[110.1,0.28],[146.8,0.14]] as[number,number][]).map(([freq,vol])=>{const osc=ctx.createOscillator();const og=ctx.createGain();osc.type="sine";osc.frequency.value=freq;og.gain.value=vol;osc.connect(og);og.connect(delay);og.connect(gain);osc.start();return osc;});const lfoNode=ctx.createOscillator();const lfoG=ctx.createGain();lfoNode.frequency.value=0.05;lfoG.gain.value=0;lfoNode.connect(lfoG);lfoG.connect(gain.gain);lfoNode.start();lfo.current=lfoNode;const merger=ctx.createChannelMerger(2);const bGain=ctx.createGain();bGain.gain.setValueAtTime(0,ctx.currentTime);bGain.gain.linearRampToValueAtTime(0,ctx.currentTime+3.5);merger.connect(bGain);bGain.connect(ctx.destination);binGain.current=bGain;const lOsc=ctx.createOscillator();const lG=ctx.createGain();lOsc.type="sine";lOsc.frequency.value=180;lG.gain.value=1;lOsc.connect(lG);lG.connect(merger,0,0);lOsc.start();const rOsc=ctx.createOscillator();const rG=ctx.createGain();rOsc.type="sine";rOsc.frequency.value=182;rG.gain.value=1;rOsc.connect(rG);rG.connect(merger,0,1);rOsc.start();binOscs.current=[lOsc,rOsc];};
 const stopAudio=()=>{if(!audioCtx.current)return;const ctx=audioCtx.current;masterGain.current?.gain.linearRampToValueAtTime(0,ctx.currentTime+1.2);binGain.current?.gain.linearRampToValueAtTime(0,ctx.currentTime+1.2);setTimeout(()=>{[...oscs.current,...binOscs.current].forEach(o=>{try{o.stop();}catch(_){}});try{lfo.current?.stop();}catch(_){}ctx.close().catch(()=>{});audioCtx.current=masterGain.current=lfo.current=binGain.current=null;oscs.current=binOscs.current=[];},1300);};
 useEffect(()=>()=>{[...oscs.current,...binOscs.current].forEach(o=>{try{o.stop();}catch(_){}});try{lfo.current?.stop();}catch(_){}audioCtx.current?.close().catch(()=>{});},[]);
 useEffect(()=>{const stop=()=>{setPlaying(false);stopAudio();};window.addEventListener("yogyism:stop",stop);return()=>window.removeEventListener("yogyism:stop",stop);},[]);// eslint-disable-line
 const handleToggle=()=>{const next=!playing;setPlaying(next);if(next){startAudio();window.dispatchEvent(new Event("yogyism:play"));}else{stopAudio();  }};
 useEffect(()=>{if(!playing){if(labelRef.current)labelRef.current.textContent="";return;}let last="";const id=setInterval(()=>{const name=stepNameRef.current;if(!name||name===last)return;last=name;if(labelRef.current)labelRef.current.textContent=name.toUpperCase();},80);return()=>clearInterval(id);},[playing]);
 return(
   <div style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",alignItems:"center"}}>
     <div style={{width:"100%",flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:4,marginBottom:"clamp(4px,0.5vw,6px)"}}>
       <button onClick={handleToggle} style={{width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",background:playing?"rgba(192,144,224,0.08)":"rgba(192,144,224,0.32)",border:`1px solid rgba(192,144,224,${playing?0.32:0.85})`,borderRadius:"50%",cursor:"pointer",color:playing?"rgba(192,144,224,0.60)":accent,transition:"all 0.2s",flexShrink:0,animation:playing?"none":"yogyismPulse_c090e0 1.3s ease-in-out infinite"}} onMouseEnter={e=>(e.currentTarget.style.background="rgba(192,144,224,0.28)")} onMouseLeave={e=>(e.currentTarget.style.background=playing?"rgba(192,144,224,0.08)":"rgba(192,144,224,0.20)")}>
         {playing?<svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><rect x="0" y="0" width="10" height="10" rx="1"/></svg>:<svg width="9" height="10" viewBox="0 0 9 10" fill="currentColor"><polygon points="0,0 9,5 0,10"/></svg>}
       </button>
       <div ref={labelRef} style={{fontFamily:FONT_MONO,fontSize:"clamp(10px,0.88vw,13px)",fontWeight:700,letterSpacing:2,color:"rgba(212,182,248,0.96)",minHeight:16}}/>
     </div>
     <div style={{flex:1,minHeight:0,width:"100%",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <BodyScanCanvasInner stepNameRef={stepNameRef} playing={playing} onComplete={()=>{setPlaying(false);stopAudio();window.dispatchEvent(new Event("yogyism:next")); }}/>
      </div>
   </div>
 );
};

const lbl:CSSProperties={fontSize:"clamp(8px,0.7vw,11px)",fontFamily:FONT_MONO,color:accent,letterSpacing:3,textTransform:"uppercase",marginBottom:"clamp(5px,0.6vw,8px)",fontStyle:"normal",flexShrink:0};
const renderSegs=(segs:Seg[])=>segs.map((s,i)=>s.b?<span key={i} style={{color:"#ffffff",fontWeight:700}}>{s.t}</span>:s.hl?<span key={i} style={{color:"#c8b0e8"}}>{s.t}</span>:s.em?<em key={i}>{s.t}</em>:<React.Fragment key={i}>{s.t}</React.Fragment>);

const YogaNidraBodyInner:React.FC=()=>{
 const { meta, body, technique, intro, threshold } = useNidra();
 const fontLinkRef = useRef<HTMLLinkElement | null>(null);
 useEffect(() => {
   const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
   if (!existing) {
     const link = document.createElement("link");
     link.rel  = "stylesheet";
     link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
     document.head.appendChild(link);
     fontLinkRef.current = link;
   }
   return () => {
     if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
       document.head.removeChild(fontLinkRef.current); fontLinkRef.current = null;
     }
   };
 }, []);

 const rootRef = useRef<HTMLDivElement>(null);

 return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}

     {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
     <div style={S.subtitleBar}>
       <div style={{
         fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
         fontFamily: FONT_BODY,
       }}>
         {body.breadcrumbPre} &nbsp;·&nbsp;{" "}
         <span style={{ color: accent }}>{body.breadcrumbAccent}</span>
       </div>
       <div style={{
         fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
         color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
         whiteSpace: "nowrap",
       }}>
         {meta.headerTitlePre}<span style={{ color: accent }}>{meta.headerTitleAccent}</span>
       </div>
     </div>

     <div style={S.body}>
       <div style={S.content}>
         <div style={{flexShrink:0}}>
           <div style={{fontSize:"clamp(8px,0.68vw,11px)",fontFamily:FONT_MONO,color:"rgba(192,144,224,0.55)",letterSpacing:3,marginBottom:"clamp(3px,0.35vw,5px)"}}>{meta.sanskrit}</div>
           <h1 style={{fontFamily:FONT_DISPLAY,fontSize:"clamp(19px,2.3vw,30px)",fontStyle:"normal",fontWeight:600,color:"#e8f4ff",margin:"0 0 clamp(4px,0.5vw,7px)",lineHeight:1.15}}>{meta.titlePre}<span style={{color:accent}}>{meta.titleAccentWord}</span>{meta.titlePost}</h1>
           <p style={{fontFamily:FONT_BODY,fontSize:"clamp(12px,1.0vw,15px)",fontStyle:"normal",fontWeight:300,color:"#9bbdd6",margin:0,lineHeight:1.6}}>{renderSegs(intro)}</p>
         </div>
         <div style={{flex:1,minHeight:0,borderTop:"1px solid rgba(192,144,224,0.12)",borderBottom:"1px solid rgba(192,144,224,0.12)",paddingTop:"clamp(6px,0.8vw,10px)",paddingBottom:"clamp(4px,0.5vw,7px)",display:"flex",flexDirection:"column",gap:"clamp(6px,0.7vw,10px)",overflowY:"hidden"}}>
           <div style={lbl}>{meta.techniqueLabel}</div>
           {technique.map((step,i)=>(
             <div key={i} style={{display:"flex",gap:"clamp(8px,0.9vw,13px)",alignItems:"flex-start",flexShrink:0}}>
               <span style={{fontFamily:FONT_MONO,fontSize:"clamp(10px,0.8vw,12px)",color:accent,opacity:0.60,flexShrink:0,lineHeight:1.6,minWidth:"1.8em"}}>{step.n}</span>
               <div style={{minWidth:0}}>
                 <span style={{fontFamily:FONT_BODY,fontSize:"clamp(11px,0.92vw,14px)",fontStyle:"normal",fontWeight:700,color:"#d8c0ff"}}>{step.title}&nbsp;</span>
                 <span style={{fontFamily:FONT_BODY,fontSize:"clamp(11px,0.9vw,14px)",fontStyle:"normal",fontWeight:300,color:"#9bbdd6",lineHeight:1.55}}>{step.desc}</span>
                 {step.sub&&<div style={{marginTop:"clamp(2px,0.3vw,4px)",fontFamily:FONT_MONO,fontSize:"clamp(9px,0.7vw,11px)",color:"rgba(192,144,224,0.52)",letterSpacing:0.2,lineHeight:1.55}}>{step.sub}</div>}
               </div>
             </div>
           ))}
         </div>
         <div style={{flexShrink:0,background:"rgba(192,144,224,0.06)",border:"1px solid rgba(192,144,224,0.22)",borderRadius:8,padding:"clamp(8px,1vw,14px) clamp(11px,1.3vw,18px)"}}>
           <div style={lbl}>{meta.thresholdLabel}</div>
           <p style={{fontFamily:FONT_BODY,fontSize:"clamp(12px,1.0vw,15px)",fontStyle:"normal",lineHeight:1.6,color:"#d0b8f0",margin:0}}>{renderSegs(threshold)}</p>
         </div>
       </div>

       {/* Panel — body-scan fills the full panel height */}
       <div style={S.panel}><BodyScanPanel/></div>
     </div>
   </div>
 );
};

const YogaNidraBodyMobile: React.FC = () => {
  const { meta } = useNidra();
  return (
  <div style={{ background:"#040f18", color:"#d0e8f8", fontFamily:font, height:"100%", width:"100%", boxSizing:"border-box", display:"flex", flexDirection:"column", padding:"18px 16px 20px", overflow:"hidden" }}>
    <div style={{ flexShrink:0, textAlign:"center", marginBottom:10 }}>
      <div style={{ fontFamily:FONT_MONO, fontSize:10, letterSpacing:2, color:"rgba(192,144,224,0.7)", textTransform:"uppercase", marginBottom:4 }}>{meta.guideBreadcrumb}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle:"normal", fontWeight:600, fontSize:22, margin:0, color:"#e8f4ff" }}>{meta.headerTitlePre}<span style={{color:accent}}>{meta.headerTitleAccent}</span></h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle:"normal", fontWeight:300, fontSize:12.5, color:"#7aa8cc", margin:"3px 0 0" }}>{meta.guideSubtitle}</p>
    </div>
    <div style={{ flex:1, minHeight:0, width:"100%", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
      <BodyScanPanel />
    </div>
  </div>
  );
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const YogaNidraBody: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <YogaNidraBodyMobile />
    : <ScaleToFit><YogaNidraBodyInner /></ScaleToFit>;
};

export default YogaNidraBody;